import React from "react";
import { Box, Typography, Paper, Grid, Button, Chip, Modal, TextField, InputAdornment } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentScope } from "../../../app/auth/authSlice";
import { useForm } from "react-hook-form";
import Spinner from "../../../Component/spinner/spinner";
import { toast } from "react-toastify";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import {
  useGetResourceByIdQuery,
  useViewResourceFileMutation,
  useDownloadResourceFileMutation,
  useApproveRejectResourceMutation
} from "../../../app/services/resourceHubServices";

export default function ViewResource() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: resourceData, isLoading, refetch } = useGetResourceByIdQuery(id);
  const resource = resourceData?.data;

  const [viewFile] = useViewResourceFileMutation();
  const [downloadFile] = useDownloadResourceFileMutation();
  const [approveRejectResource] = useApproveRejectResourceMutation();
  const memberScope = useSelector(selectCurrentScope);

  const { register: registerReject, handleSubmit: handleRejectSubmit, watch, formState: { errors } } = useForm();
  const [openReject, setOpenReject] = React.useState(false);

  const handleApprove = async () => {
    try {
      const res = await approveRejectResource({ id, data: { action: 'approve' } }).unwrap();
      if (res.status) {
        toast.success(res.message);
        navigate(-1);
      } else {
        toast.error(res.err?.message || "Failed to approve resource");
      }
    } catch (error) {
      toast.error("An error occurred while approving");
    }
  };

  const onReject = async (data) => {
    try {
      const res = await approveRejectResource({ id, data: { action: 'reject', rejectReason: data.rejectionReason } }).unwrap();
      if (res.status) {
        toast.success(res.message);
        setOpenReject(false);
        navigate(-1);
      } else {
        toast.error(res.err?.message || "Failed to reject resource");
      }
    } catch (error) {
      toast.error("An error occurred while rejecting");
    }
  };

  const handleFileAction = async (action, file) => {
    try {
      let data;
      if (action === "view") {
        data = await viewFile({ resourceId: id, fileId: file?.fileId }).unwrap();
      } else {
        data = await downloadFile({ resourceId: id, fileId: file?.fileId }).unwrap();
      }

      if (data.status && data.data) {
        if (action === 'view') {
          window.open(file?.fileUrl, "_blank");
        } else {
          try {
            // Append a cache-buster so that if the image was already loaded by an <img> tag (without CORS),
            // the fetch doesn't fail due to an opaque cache hit.
            const fetchUrl = file?.fileUrl + (file?.fileUrl.includes('?') ? '&' : '?') + 'download=1&t=' + new Date().getTime();
            const response = await fetch(fetchUrl);
            if (!response.ok) throw new Error("Network response was not ok");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.fileName || file.originalFileName || 'download';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (fetchErr) {
            const link = document.createElement('a');
            link.href = file?.fileUrl;
            link.target = '_blank';
            link.download = file.fileName || file.originalFileName || '';
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        }
        // Refetch to update counts
        refetch();
      } else {
        toast.error(data.message || `Failed to ${action} file`);
      }
    } catch (error) {
      toast.error(`Error performing ${action}`);
    }
  };

  // Returns a coloured MUI icon component based on the file extension
  const getFileIcon = (format = '') => {
    const src = (format).toLowerCase();
    if (/(jpg|jpeg|png|gif|webp|bmp|svg)/.test(src)) {
      return <ImageIcon style={{ fontSize: 28, color: '#1976d2' }} />;
    }
    if (/(pdf)/.test(src)) {
      return <PictureAsPdfIcon style={{ fontSize: 28, color: '#d32f2f' }} />;
    }
    if (/(doc|docx)/.test(src)) {
      return <DescriptionIcon style={{ fontSize: 28, color: '#3f51b5' }} />;
    }
    return <AttachFileIcon style={{ fontSize: 28, color: '#757575' }} />;
  };

  const extractYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (isLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><Spinner /></Box>;
  }

  if (!resource) {
    return <Box p={3}><Typography>Resource not found.</Typography></Box>;
  }

  const youtubeId = extractYoutubeVideoId(resource.youtubeLink);

  return (
    <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">View Resource</h2>
        </div>
      </div>

      <Box p={{ xs: 2, md: 5 }} className="overallPadding">
        <div className="text-end w-100">
          <Button variant="contained" className="menus-color poppins-regular" style={{ color: "white", marginBottom: "15px" }} onClick={() => memberScope === 'PORTAL_MEMBER' ? navigate('../resources') : navigate('../resource-hub')}>
            Back
          </Button>
        </div>
        <Grid container spacing={4}>
          {/* Main Content Area */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {resource.name}
                </Typography>
                {resource.resourceCategoryId && (
                  <Chip label={resource.resourceCategoryId.name} color="secondary" variant="outlined" />
                )}
              </Box>

              <Typography variant="body1" color="text.secondary" dangerouslySetInnerHTML={{ __html: resource.description || "No description provided." }} sx={{ mb: 4, lineHeight: 1.8 }}>
              </Typography>

              {youtubeId && (
                <Box mt={4}>
                  <div className="d-flex justify-content-between align-items-center">
                    <Typography variant="h6" fontWeight="bold" mb={2}>Video Resource</Typography>
                    <Typography variant="p" color="text.secondary" className="d-flex align-items-center gap-1" fontWeight="light" mb={2}><VisibilityIcon /> {resource?.youtubeViewCount}</Typography>
                  </div>
                  <Box sx={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 3, boxShadow: 3 }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeId}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    />
                  </Box>
                </Box>
              )}

              {/* Attachments Area */}
              <Box mt={4} pt={4} borderTop="1px solid #eee">
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Resource Files ({resource.files?.length || 0})
                </Typography>

                {resource.files && resource.files.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={4}>
                    {resource.files.map((file) => {
                      const format = (file.fileFormat || '').toLowerCase();
                      const isImage = /(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(format);
                      const isPdf = /(pdf)/i.test(format);
                      const isDoc = /(doc|docx)/i.test(format);

                      return (
                        <Box key={file.fileId} sx={{ border: '1px solid #eee', borderRadius: 3, overflow: 'hidden', boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.02)' }}>
                          <Box sx={{ bgcolor: '#f9f9f9', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', flexWrap: 'wrap', gap: 2 }}>
                            <Box display="flex" alignItems="center">
                              <Box mr={2} display="flex" alignItems="center">{getFileIcon(file.fileFormat)}</Box>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">{file.fileName}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">Size: {(file.fileSize / 1024 / 1024).toFixed(2)} MB</Typography>
                                <Box display="flex" gap={2} mt={0.5}>
                                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                                    <VisibilityIcon fontSize="inherit" sx={{ mr: 0.5 }} /> {file.viewCount || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                                    <DownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} /> {file.downloadCount || 0}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Box display="flex" gap={1}>
                              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleFileAction('view', file)}>
                                View
                              </Button>
                              <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={() => handleFileAction('download', file)}>
                                Download
                              </Button>
                            </Box>
                          </Box>
                          <Box sx={{ p: isImage ? 3 : 0, display: 'flex', justifyContent: 'center', bgcolor: isImage ? '#f4f6f8' : '#fff', height: isImage ? 'auto' : '600px' }}>
                            {isImage ? (
                              <img src={file.fileUrl} alt={file.fileName} style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            ) : isPdf ? (
                              <iframe src={file.fileUrl} title={file.fileName} width="100%" height="100%" style={{ border: 'none' }} />
                            ) : isDoc ? (
                              <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.fileUrl)}`} title={file.fileName} width="100%" height="100%" style={{ border: 'none' }} />
                            ) : (
                              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4}>
                                <Typography variant="body1" color="text.secondary" mb={2}>Preview not available for this file format ({file.fileFormat}).</Typography>
                                <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleFileAction('download', file)}>Download to View</Button>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No files attached to this resource.</Typography>
                )}
              </Box>

              <Box mt={4} pt={3} borderTop="1px solid #eee" display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Added on: <span className="text-body"> {new Date(resource.createdAt).toLocaleDateString('en-IN')}</span>
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  {memberScope && (memberScope === 'PORTAL_SUPER_ADMIN' || memberScope === 'PORTAL_ADMIN') && resource.isApproved === 0 && (
                    <>
                      <Button variant="contained" color="success" sx={{ mr: 2 }} onClick={handleApprove}>Approve</Button>
                      <Button variant="contained" color="error" onClick={() => setOpenReject(true)}>Reject</Button>
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>


      <Modal open={openReject} onClose={() => setOpenReject(false)}>
        <Box component="form" onSubmit={handleRejectSubmit(onReject)} sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
        }}>
          <Typography variant="h6" color="error" mb={2} fontWeight="bold">Reject Resource</Typography>
          <TextField
            fullWidth
            label="Reason For Rejection *"
            multiline
            rows={4}
            {...registerReject("rejectionReason", { required: "Please enter reject reason" })}
            error={!!errors?.rejectionReason}
            helperText={errors?.rejectionReason?.message}
            inputProps={{ maxLength: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {300 - (watch("rejectionReason")?.length || 0)}
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button variant="outlined" color="inherit" onClick={() => setOpenReject(false)}>Cancel</Button>
            <Button variant="contained" color="error" type="submit">Submit Rejection</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
